import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { InputField, FormattedNumberInputField, ComboboxInputField } from "@/components/shared";
import { ComputationType, DecisionType, ComparatorType } from "@/graphql/__generated__/graphql";
import { bidComputationTypes, percentageInputKeys, currencyInputKeys, monthSuffixComputationTypes, allowNullInputKeys, shouldAllowNullComparator } from "../constants";
import { TFunction } from "i18next";
import { UseFormReturn } from "react-hook-form";
import { useEffect } from "react";

function ValueInput({
  computationType,
  comparatorType,
  form,
  path,
  index,
  t
}: {
  computationType?: ComputationType;
  comparatorType?: ComparatorType;
  form: UseFormReturn<any>;
  path: string;
  index: number;
  t: TFunction
}) {
  // Handle string input type
  const isStringInput = computationType === undefined;

  if (isStringInput) {
    return (
      <InputField
        name={`${path}.conditions.${index}.value`}
        placeholder={t("settings.rulebuilder.create.inputs.value.placeholder")}
        form={form}
        className="w-full"
      />
    );
  }

  const isNullInput = allowNullInputKeys.includes(computationType) && comparatorType === ComparatorType.IsNull;

  // Determine input configuration
  const isCurrency = currencyInputKeys.includes(computationType);
  const isPercentage = percentageInputKeys.includes(computationType);
  const isMonthSuffix = monthSuffixComputationTypes.includes(computationType);

  const type = isCurrency ? "currency" : "decimal";
  const suffix = isMonthSuffix ? t("settings.rulebuilder.months") : isPercentage ? "%" : "";
  const placeholder = isPercentage
    ? t("settings.rulebuilder.create.inputs.value.marginplaceholder")
    : isMonthSuffix
      ? ""
      : t("settings.rulebuilder.create.inputs.value.bidplaceholder");

  return (
    <FormattedNumberInputField
      key={computationType}
      name={`${path}.conditions.${index}.value`}
      placeholder={placeholder}
      control={form.control}
      style={type}
      additionalSuffix={suffix}
      label="Value"
      hideLabel={true}
      isStandalone={true}
      spaceBetweenSuffix={isMonthSuffix}
      className="w-full"
      disabled={isNullInput}
    />
  );
}

/**
 * Props for the ConditionComponent
 * @interface ConditionComponentProps
 * @property {UseFormReturn<any>} form - The form object from react-hook-form
 * @property {number} index - The index of the condition in the conditions array
 * @property {Function} [onDelete] - Optional callback function to delete the condition
 * @property {string} path - The path to the condition in the form data structure
 * @property {string} ruleType - The type of rule (margin or bid)
 *
 */
interface ConditionComponentProps {
  form: UseFormReturn<any>;
  index: number;
  onDelete?: () => void;
  path: string;
  ruleType: string;
  t: TFunction;
}

/**
 * Component for rendering a single condition in the rule builder
 *
 * Allows users to select computation type, operator, and enter a value for the condition.
 * Different input types are rendered based on the selected computation type.
 *
 * @param {ConditionComponentProps} props - The component props
 * @returns {JSX.Element} The rendered condition component
 */
function ConditionComponent({
  form,
  index,
  onDelete,
  path,
  ruleType,
  t,
}: ConditionComponentProps) {
  const selectableComputationTypes = ruleType === DecisionType.Bid ? bidComputationTypes : ComputationType;

  const computationType = form.watch(`${path}.conditions.${index}.computationType`);
  const comparatorType = form.watch(`${path}.conditions.${index}.operator`);

  // Watch both computationType and comparatorType changes
  useEffect(() => {
    // Case 1: Switching to a computation type that doesn't allow null
    if (comparatorType === ComparatorType.IsNull && !shouldAllowNullComparator(computationType)) {
      form.setValue(`${path}.conditions.${index}.operator`, undefined);
      form.setValue(`${path}.conditions.${index}.value`, undefined);
    }
    // Case 2: Switching to IsNull operator with allowed computation type
    else if (comparatorType === ComparatorType.IsNull && shouldAllowNullComparator(computationType)) {
      form.setValue(`${path}.conditions.${index}.value`, "null");
      form.clearErrors(`${path}.conditions.${index}.value`);
    }
    // Case 3: Switching away from IsNull operator
    else if (form.getValues(`${path}.conditions.${index}.value`) === "null") {
      form.setValue(`${path}.conditions.${index}.value`, undefined);
    }
  }, [computationType, comparatorType, form, path, index]);

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-center">
        {index === 0 ? (
          <div className="text-base font-bold text-slate-600 mb-2 flex flex-col items-center">
            {index !== 0 && (
              <div className="w-0.5 h-2 bg-slate-200 mb-1"></div>
            )}
            <span>{t("settings.rulebuilder.if")}</span>
            <div className="w-0.5 h-2 bg-slate-200 mt-1"></div>
          </div>
        ) : (
          <div className="text-base font-bold text-slate-600 mb-2 flex flex-col items-center">
            <div className="w-0.5 h-2 bg-slate-200 mb-1"></div>
            <span>{t("settings.rulebuilder.and")}</span>
            <div className="w-0.5 h-2 bg-slate-200 mt-1"></div>
          </div>
        )}
      </div>

      <div className="flex items-start gap-2">
        <div className="flex flex-1 items-start gap-2 min-w-0">
          <div className="w-[33%] shrink-0">
            <ComboboxInputField
              name={`${path}.conditions.${index}.computationType`}
              placeholder={t("settings.rulebuilder.create.inputs.computationtype.placeholder")}
              form={form}
              className="w-full"
              options={Object.values(selectableComputationTypes).map((computation) => ({
                label: t(`common:computationtype.${computation.toLowerCase()}`),
                value: computation
              }))}
            />
          </div>
          <div className="w-[33%] shrink-0">
            <ComboboxInputField
              name={`${path}.conditions.${index}.operator`}
              placeholder={t("settings.rulebuilder.create.inputs.operator.placeholder")}
              form={form}
              className="w-full"
              options={Object.values(ComparatorType)
                .filter(operator => {
                  // Filter out string comparison operators
                  if (operator === ComparatorType.Contains || operator === ComparatorType.NotContains) {
                    return false;
                  }
                  // Only include IsNull if computationType is in allowNullInputKeys
                  if (operator === ComparatorType.IsNull) {
                    return allowNullInputKeys.includes(computationType);
                  }
                  return true;
                })
                .map((operator) => ({
                  label: t(`common:operator.${operator.toLowerCase()}`),
                  value: operator
                }))}
            />
          </div>

          {/* Render different input types based on the selected computation type */}
          <div className="w-[33%] shrink-0">
            <div className="mb-2">
              <ValueInput computationType={computationType} comparatorType={comparatorType} form={form} path={path} index={index} t={t} />
            </div>
          </div>
        </div>

        {onDelete ? (
          <div className="shrink-0 ml-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onDelete}
              className="mb-2 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        ) : (
          <div className="shrink-0 h-10 w-10 mb-2 ml-2" />
        )}
      </div>
    </div>
  );
}

export default ConditionComponent;
